import React from 'react'
import { Modal, Form, Input, Button, notification } from 'antd'
import styled from 'styled-components'
import ApiClient from '@quiqupltd/quiqupjs/lib/network/api-client'
import { useMutation } from 'react-query'
import theme from '../../../theme'
import { useEnvVars } from '../../../hooks/useEnvVars'
import { useTracking } from '../../../hooks/useTracking'
import { Destination } from '../../../types/Order'

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
  }
`

const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${theme.midnight800};
  margin-bottom: 8px;
`

const ModalSubtitle = styled.div`
  font-size: 14px;
  color: ${theme.slate500};
  margin-bottom: 24px;
`

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 8px;
`

const NATIONAL_ADDRESS_REGEX = /^[A-Za-z]{4}[0-9]{4}$/

type Props = {
  isOpen: boolean
  onClose: () => void
  orderUuid: string
  destination: Destination
  currentNationalAddress?: string
}

export default function UpdateNationalAddressModal(props: Props) {
  const [form] = Form.useForm()
  const { env } = useEnvVars()
  const { getOrder } = useTracking()
  const [api, contextHolder] = notification.useNotification()

  const mutation = useMutation({
    mutationFn: (nationalAddress: string) => {
      const address = props.destination.address
      return ApiClient.put({
        path: `${env.EXCORE_URL}/customers/orders/${props.orderUuid}`,
        includeAuth: false,
        data: {
          address1: address.address1,
          address2: address.address2,
          town: address.town,
          country: address.country,
          coordinates: {
            lat: String(address.coordinates.lat),
            lng: String(address.coordinates.lng),
          },
          apartment_number: address.apartmentNumber,
          ksa_national_address: nationalAddress.toUpperCase(),
          waypoint_type: 'destination',
        },
      })
    },
    onSuccess: () => {
      getOrder({
        orderId: props.orderUuid,
        replace: true,
        triggerAlert: false,
      })
      props.onClose()
      form.resetFields()
    },
    onError: () => {
      api.error({
        message: 'Something went wrong',
        description:
          'We could not update the national address, please try again later.',
      })
    },
  })

  const validateNationalAddress = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Please enter a national address'))
    }
    if (!NATIONAL_ADDRESS_REGEX.test(value)) {
      return Promise.reject(
        new Error(
          'National address must be 4 letters followed by 4 digits (e.g. ABCD1234)'
        )
      )
    }
    return Promise.resolve()
  }

  const handleSubmit = (values: { nationalAddress: string }) => {
    mutation.mutate(values.nationalAddress)
  }

  const handleClose = () => {
    form.resetFields()
    props.onClose()
  }

  return (
    <>
      {contextHolder}
      <StyledModal
        open={props.isOpen}
        onCancel={handleClose}
        footer={null}
        centered
        destroyOnClose
      >
        <ModalTitle>Update National Address</ModalTitle>
        <ModalSubtitle>
          Enter your Saudi National Address to proceed with delivery
        </ModalSubtitle>
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          initialValues={{
            nationalAddress: props.currentNationalAddress || '',
          }}
        >
          <Form.Item
            name="nationalAddress"
            label="National Address"
            rules={[{ validator: validateNationalAddress }]}
          >
            <Input placeholder="eg. ABCD1234" maxLength={8} />
          </Form.Item>
          <SubmitButton
            type="primary"
            htmlType="submit"
            loading={mutation.isLoading}
            disabled={mutation.isLoading}
          >
            Update National Address
          </SubmitButton>
        </Form>
      </StyledModal>
    </>
  )
}
